import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Row, Col, Button } from 'react-bootstrap';
import { injectIntl } from 'react-intl';
import langs from 'langs';

import { formStatusDispatchHelper, formReset } from 'actions/FormStatusActions';
import { addNotification } from 'actions/NotificationsActions';

import EntityStateUpdater from 'component/EntityStateUpdater';

import Breadcrumb from 'components/layout/Breadcrumb';
import Loader from 'components/layout/Loader';
import QuestionnaireQuestionDraggable from 'components/layout/questionnaire/QuestionnaireQuestionDraggable';
import ShareableResource from 'components/layout/shareable/ShareableResource';

import QuestionnaireQuestionEdit from 'components/modals/QuestionnaireQuestionEdit';

import FormErrors from 'components/forms/FormErrors';
import FormInput from 'components/forms/FormInput';
import FormLanguageSelector from 'components/forms/FormLanguageSelector';
import FormRichEditor from 'components/forms/FormRichEditor';

import Entity from 'components/Entity';
import Data from 'components/Data';

import { entityMetaMapping } from 'js/constants';

import DocMeta from 'components/DocMeta';
import { generateMetaTags } from 'helpers/metaTags';

class QuestionnaireDetails extends EntityStateUpdater {
  constructor(props) {
    super(props);

    this.state = {
      showQuestionnaireQuestionEdit: false,
      editedQuestionIndex: null,
      entityLoaded: false,
      entityChanged: false,
      showLanguageSelection: false,
    };

    this.entity = new Entity();

    // if we get user data from the outside, we can pass it on to our data entity
    if (props.questionnaireData) {
      this.entity.setData(props.questionnaireData);
    }

    if (
      props.meta[entityMetaMapping.questionnaire] !== undefined &&
      !props.meta[entityMetaMapping.questionnaire].pending
    ) {
      this.entity.setMeta(props.meta, entityMetaMapping.questionnaire);
    }

    if (this.entity.getDataLoaded() && this.entity.getMetaLoaded()) {
      this.state = {
        ...this.state,
        entityLoaded: true,
        entity: this.entity,
      };
    }
  }

  showLanguageSelection = e => {
    e.preventDefault();
    this.setState({ showLanguageSelection: true });
  };

  closeQuestionnaireQuestionEdit = () => {
    this.setState({ showQuestionnaireQuestionEdit: false });
  };

  showEditQuestion = index => {
    this.setState({
      editedQuestionIndex: index,
      showQuestionnaireQuestionEdit: true,
    });
  };

  addQuestion = () => {
    const { entity } = this.state;
    const showIndex = entity.getQuestions().length;

    const newQuestion = entity.createValue('questions', [], { type: 'text_input', showInLeadDescription: true });
    const newValue = entity.getQuestions().slice();
    newValue.push(newQuestion);
    let newEntity = { ...entity };
    newEntity = newEntity.updateValue('questions', newValue, []);

    this.changeEntity(newEntity, () => {
      this.showEditQuestion(showIndex);
    });
  };

  removeQuestion = index => {
    const { entity } = this.state;
    const newQuestionsArray = entity.getQuestions().slice();
    newQuestionsArray.splice(index, 1);
    const newEntity = { ...entity.updateValue('questions', newQuestionsArray, []) };

    this.changeEntity(newEntity);
  };

  moveQuestion = (dragIndex, hoverIndex) => {
    const { entity } = this.state;
    const newQuestionsArray = entity.getQuestions().slice();
    const tmpValue = { ...newQuestionsArray[hoverIndex] };
    newQuestionsArray[hoverIndex] = { ...newQuestionsArray[dragIndex] };
    newQuestionsArray[dragIndex] = tmpValue;

    const newEntity = { ...entity.updateValue('questions', newQuestionsArray, []) };
    this.changeEntity(newEntity);
  };

  getQuestion = questionId => {
    const { data } = this.props;
    for (let i = 0; i < data.data.questions.length; i++) {
      if (data.data.questions[i].id === questionId) {
        return data.data.questions[i];
      }
    }

    return null;
  };

  componentDidUpdate = prevProps => {
    const {
      intl: { messages },
      meta,
      data,
      formStatus,
      doNotify,
    } = this.props;

    // client data was saved and there was no error
    if (prevProps.formStatus.pending && !formStatus.pending && !formStatus.hasError) {
      this.setState({ entityChanged: false });
      doNotify(1, messages.questionnaire_data_saved);
    }

    if (prevProps.data.pending && !data.pending) {
      this.entity.setData(data.data);
      if (this.entity.getMetaLoaded()) {
        this.setState({ entityLoaded: true, entity: this.entity });
      }
    }

    if (
      meta[entityMetaMapping.questionnaire] !== undefined &&
      !meta[entityMetaMapping.questionnaire].pending &&
      !this.entity.getMetaLoaded()
    ) {
      this.entity.setMeta(meta, entityMetaMapping.questionnaire);
      if (this.entity.getDataLoaded()) {
        this.setState({ entityLoaded: true, entity: this.entity });
      }
    }
  };

  formSubmit = e => {
    e.preventDefault();

    this.setState(
      prevState => ({ entity: prevState.entity.validate() }),
      () => {
        const { match, onSubmit } = this.props;
        const { entity } = this.state;

        if (entity.isValid) {
          onSubmit(match.params.questionnaireId, entity.processedData);
        }
      }
    );
  };

  render() {
    const {
      intl: { messages },
      intl,
      appIntl,
      match,
      formStatus,
      data,
      user,
    } = this.props;
    const { entityLoaded, entityChanged, entity, showLanguageSelection } = this.state;

    let metaTitle = messages.questionnaire;
    if (entityLoaded) {
      metaTitle += ' - ' + entity.getName();
    }

    return (
      <DocMeta tags={generateMetaTags(messages.meta.questionnaire_details, metaTitle)}>
        <div className="padding-25 sm-padding-10">
          <Data
            dataName="questionnaire"
            url={'/api/questionnaires/' + match.params.questionnaireId + '.json'}
            data={data}
          />

          <Breadcrumb
            crumbs={[
              { link: appIntl.urlPrefix + 'account', name: 'Dashboard' },
              { link: appIntl.urlPrefix + 'account/settings', name: messages.settings },
              { link: appIntl.urlPrefix + 'account/settings/questionnaires', name: messages.questionnaires },
              { link: null, name: entityLoaded ? entity.getName() : null },
            ]}
            className="p-t-0"
          />

          <FormErrors formStatus={formStatus} />

          {entityLoaded ? (
            <Row>
              <form onSubmit={this.formSubmit}>
                <div className="text-right m-b-10">
                  <ShareableResource
                    type="questionnaire"
                    id={match.params.questionnaireId}
                    className="text-master m-r-20"
                  />

                  <Button
                    disabled={formStatus.pending}
                    type="submit"
                    bsStyle={entityChanged ? 'primary' : 'default'}
                    bsSize="lg"
                  >
                    <i className={'fa-fw ' + (formStatus.pending ? ' fa fa-circle-notch fa-spin' : 'fal fa-save')} />{' '}
                    {messages.save}
                  </Button>
                </div>

                <Col xs={12} md={6}>
                  <p className="form-group-title">{messages.questionnaire}</p>
                  <div className="form-group-attached">
                    <FormInput
                      label={messages.name}
                      name="name"
                      value={entity.getName()}
                      meta={entity.getNameMetaInformation()}
                      validation={entity.getNameValidationResult()}
                      onChange={this.updateEntity}
                      disabled={formStatus.pending}
                    />
                    <FormRichEditor
                      label={messages.description}
                      name="description"
                      value={entity.getDescription()}
                      meta={entity.getDescriptionMetaInformation()}
                      validation={entity.getDescriptionValidationResult()}
                      onChange={this.updateEntity}
                      disabled={formStatus.pending}
                    />
                    {showLanguageSelection ? (
                      <FormLanguageSelector onChange={this.updateEntity} value={entity.getLang()} name="lang" />
                    ) : (
                      <div className="form-group form-group-default p-t-10 p-b-10">
                        <a href="#showLanguageSelection" className="text-master" onClick={this.showLanguageSelection}>
                          {messages.edit_language} ({langs.where('1', entity.getLang()).local}){' '}
                          <i className="fal fa-chevron-right m-l-10" />
                        </a>
                      </div>
                    )}
                  </div>
                </Col>

                <Col xs={12}>
                  {entity.getQuestions().length > 0 && (
                    <React.Fragment>
                      <p className="m-t-15">{messages.questions}</p>
                      {entity.getQuestions().map((question, i) => {
                        return (
                          <QuestionnaireQuestionDraggable
                            key={i}
                            index={i}
                            intl={intl}
                            id={question.getId()}
                            label={question.getLabel()}
                            type={question.getType()}
                            isRequired={question.getIsRequired()}
                            showInLeadDescription={question.getShowInLeadDescription()}
                            options={question.getOptions()}
                            moveQuestion={this.moveQuestion}
                            editQuestion={this.showEditQuestion}
                            removeQuestion={this.removeQuestion}
                            user={user}
                          />
                        );
                      })}
                    </React.Fragment>
                  )}

                  <div className="m-t-10">
                    <Button onClick={this.addQuestion}>{messages.add_question}</Button>
                  </div>
                </Col>
              </form>
            </Row>
          ) : (
            <Loader />
          )}

          <div id="edit-question">
            <QuestionnaireQuestionEdit
              updateEntity={this.updateEntity}
              entity={entity}
              show={this.state.showQuestionnaireQuestionEdit}
              close={this.closeQuestionnaireQuestionEdit}
              onSubmitParent={this.closeQuestionnaireQuestionEdit}
              questionIndex={this.state.editedQuestionIndex}
            />
          </div>
        </div>
      </DocMeta>
    );
  }
}

const mapStateToProps = state => {
  return {
    appIntl: state.intl,
    data: state.data.questionnaire,
    meta: state.entityMeta,
    formStatus: state.formStatus.questionnaireEdit,
    user: state.login.user,
  };
};
const mapDispatchToProps = dispatch => {
  return {
    doNotify: (code, text) => {
      dispatch(addNotification(code, text));
    },
    onSubmit: (questionnaireId, args) => {
      formStatusDispatchHelper(
        'questionnaireEdit',
        '/api/questionnaires/' + questionnaireId + '.json',
        args,
        'put',
        dispatch
      );
    },
    onFormSuccessfull: () => {
      dispatch(formReset('questionnaireEdit'));
    },
  };
};

export default withRouter(
  injectIntl(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(QuestionnaireDetails)
  )
);
